import React, { useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Download, TrendingUp, TrendingDown, Clock } from 'lucide-react'
import { useTradingSimulation } from '../../hooks/useTradingSimulation'

interface OrderHistoryPanelProps {
  onOrderExecuted?: () => void
}

const OrderHistoryPanel: React.FC<OrderHistoryPanelProps> = () => {
  const {
    orderHistory,
    loading,
    error,
    fetchOrderHistory
  } = useTradingSimulation()

  const [filter, setFilter] = React.useState<'all' | 'buy' | 'sell'>('all')

  useEffect(() => {
    fetchOrderHistory()
  }, [fetchOrderHistory])

  const filteredOrders = orderHistory.filter(order => {
    if (filter === 'all') return true
    return order.orderType === filter
  })

  const exportToCSV = () => {
    const headers = ['Date', 'Symbol', 'Type', 'Quantity', 'Price', 'Total Amount', 'Status']
    const csvContent = [
      headers.join(','),
      ...filteredOrders.map(order => [
        new Date(order.createdAt).toLocaleDateString(),
        order.symbol,
        order.orderType.toUpperCase(),
        order.quantity,
        order.price.toFixed(2),
        order.totalAmount.toFixed(2),
        order.status
      ].join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `trading-history-${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-background-900/50 to-background-800/50 backdrop-blur-sm rounded-3xl p-6 border border-background-700/50 shadow-2xl"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center space-x-4">
          <div className="p-3 bg-primary-500/20 rounded-xl">
            <Clock className="w-6 h-6 text-primary-400" />
          </div>
          <h3 className="text-xl font-bold text-background-100">Order History</h3>
        </div>
        <div className="flex items-center space-x-3">
          {/* Filter */}
          <div className="flex space-x-1 bg-background-800/50 backdrop-blur-sm rounded-xl p-1">
            {(['all', 'buy', 'sell'] as const).map((filterType) => (
              <motion.button
                key={filterType}
                onClick={() => setFilter(filterType)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`px-4 py-2 text-sm font-semibold rounded-lg transition-all duration-300 ${
                  filter === filterType
                    ? 'bg-primary-500 text-white shadow-lg'
                    : 'text-background-400 hover:text-background-200 hover:bg-background-700/50'
                }`}
              >
                {filterType.charAt(0).toUpperCase() + filterType.slice(1)}
              </motion.button>
            ))}
          </div>
          {/* Export Button */}
          <motion.button
            onClick={exportToCSV}
            disabled={filteredOrders.length === 0}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="p-3 hover:bg-background-700/50 rounded-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Export to CSV"
          >
            <Download className="w-5 h-5 text-background-400" />
          </motion.button>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-12">
          <motion.div 
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            className="w-12 h-12 border-4 border-primary-500/30 border-t-primary-500 rounded-full mx-auto mb-6"
          />
          <p className="text-background-400 text-lg font-medium">Loading order history...</p>
        </div>
      )}

      {/* Error State */}
      <AnimatePresence>
        {error && (
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl mb-6"
          >
            <p className="text-red-400 text-sm font-medium">{error}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Orders List */}
      {!loading && filteredOrders.length === 0 && (
        <div className="text-center py-16">
          <Clock className="w-16 h-16 text-background-600 mx-auto mb-6" />
          <p className="text-background-400 mb-3 text-lg font-medium">No orders found</p>
          <p className="text-background-500 text-sm">
            {filter === 'all' 
              ? 'Start trading to see your order history here'
              : `No ${filter} orders found`
            }
          </p>
        </div>
      )}

      {!loading && filteredOrders.length > 0 && (
        <div className="space-y-4">
          {filteredOrders.map((order, index) => {
            const { date, time } = formatDate(order.createdAt)
            const isBuy = order.orderType === 'buy'
            
            return (
              <motion.div
                key={order.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ scale: 1.02, y: -2 }}
                className="flex items-center justify-between p-5 bg-background-800/50 backdrop-blur-sm rounded-2xl border border-background-700/30 hover:border-background-600/50 hover:bg-background-700/50 transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                <div className="flex items-center space-x-5">
                  {/* Order Type Icon */}
                  <div className={`p-3 rounded-xl shadow-lg ${
                    isBuy 
                      ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                      : 'bg-red-500/20 text-red-400 border border-red-500/30'
                  }`}>
                    {isBuy ? (
                      <TrendingUp className="w-5 h-5" />
                    ) : (
                      <TrendingDown className="w-5 h-5" />
                    )}
                  </div>

                  {/* Order Details */}
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-1">
                      <span className="text-lg font-bold text-background-100">
                        {order.symbol}
                      </span>
                      <span className={`px-3 py-1 text-xs font-bold rounded-xl ${
                        isBuy
                          ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                          : 'bg-red-500/20 text-red-400 border border-red-500/30'
                      }`}>
                        {isBuy ? 'BUY' : 'SELL'}
                      </span>
                    </div>
                    <div className="text-sm text-background-400 font-medium">
                      {order.quantity} shares @ ${order.price.toFixed(2)}
                    </div>
                    <div className="text-xs text-background-500 font-medium">
                      {date} at {time}
                    </div>
                  </div>
                </div>

                {/* Order Amount and Status */}
                <div className="text-right">
                  <div className="text-lg font-bold text-background-100 mb-1">
                    ${order.totalAmount.toFixed(2)}
                  </div>
                  <div className={`text-sm font-semibold px-3 py-1 rounded-xl ${
                    order.status === 'completed' 
                      ? 'text-green-400 bg-green-500/10 border border-green-500/20' 
                      : order.status === 'pending'
                      ? 'text-yellow-400 bg-yellow-500/10 border border-yellow-500/20'
                      : 'text-red-400 bg-red-500/10 border border-red-500/20'
                  }`}>
                    {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>
      )}

      {/* Summary */}
      {!loading && filteredOrders.length > 0 && (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8 pt-6 border-t border-background-700/50"
        >
          <div className="grid grid-cols-3 gap-6 text-center">
            <motion.div 
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.3 }}
              className="p-4 bg-background-800/30 rounded-2xl border border-background-700/30"
            >
              <div className="text-2xl font-bold text-background-100 mb-1">
                {filteredOrders.length}
              </div>
              <div className="text-sm text-background-400 font-medium">Total Orders</div>
            </motion.div>
            <motion.div 
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.4 }}
              className="p-4 bg-green-500/10 rounded-2xl border border-green-500/20"
            >
              <div className="text-2xl font-bold text-green-400 mb-1">
                {filteredOrders.filter(o => o.orderType === 'buy').length}
              </div>
              <div className="text-sm text-background-400 font-medium">Buy Orders</div>
            </motion.div>
            <motion.div 
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.5 }}
              className="p-4 bg-red-500/10 rounded-2xl border border-red-500/20"
            >
              <div className="text-2xl font-bold text-red-400 mb-1">
                {filteredOrders.filter(o => o.orderType === 'sell').length}
              </div>
              <div className="text-sm text-background-400 font-medium">Sell Orders</div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </motion.div>
  )
}

export default OrderHistoryPanel
